import React from 'react'
import PropTypes from 'prop-types'

import { YES_NO_OPTIONS } from 'utils/enums/questions'
import { FormRadioButtonGroup } from 'components/form/radioButtonGroup/FormRadioButtonGroup'
import { validateRequiredField } from 'utils/helpers/validators/byType/requiredField'

import styles from '../FormByState.module.scss'
import { map, pickBy } from 'lodash'
import { Col, Row } from 'components/grid'
import { UploadScan } from 'components/uploadScan/UploadScan'

const MAX_SCANS_COUNT = 5

const generateScanFields = (fieldName, scansCount) => {
  const result = {}

  for (let i = 1; i <= scansCount; i++) {
    const key = `${fieldName}Scan${i}`
    if (i === 1) {
      result[key] = {
        required: formData => !!formData[fieldName]?.value,
        validator: validateRequiredField,
      }
    } else {
      result[key] = {}
    }
  }

  return result
}

export const NmFormConfig = {
  hadBeenUnderInvestigationForChildAbuse: {
    required: formData => !formData.unknownPerson.value,
    validator: validateRequiredField,
  },
  ...generateScanFields('hadBeenUnderInvestigationForChildAbuse', 5),

  hadBeenUnderInvestigationReEthicalRule: {
    required: formData => !formData.unknownPerson.value,
    validator: validateRequiredField,
  },
  ...generateScanFields('hadBeenUnderInvestigationReEthicalRule', 5),

  hadLicenseDenied: {
    required: formData => !formData.unknownPerson.value,
    validator: validateRequiredField,
  },
  ...generateScanFields('hadLicenseDenied', 5),
}

const ScanByKey = ({ scanKey, formData, formId, setIsFormBusy, handleUploadScan, handleDeleteScan }) => {
  const files = map(
    pickBy(formData, field => field.name.startsWith(scanKey) && field.value),
    field => field.value,
  )

  return (
    <Row topIndent bottomIndentLarge>
      <Col>
        <UploadScan
          title="Please upload documentation or statement."
          showHint
          formId={formId}
          files={files}
          onIsBusyChanged={setIsFormBusy}
          onUpload={scan => handleUploadScan(scanKey, files.length, scan)}
          onRemove={index => handleDeleteScan(scanKey, files.length, index)}
          disabled={files.length === MAX_SCANS_COUNT}
        />
      </Col>
    </Row>
  )
}

export const NMForm = ({ formData, handleFieldChange, handleUploadScan, handleDeleteScan, setIsFormBusy, formId }) => (
  <div className={styles.form}>
    <FormRadioButtonGroup
      title="Has the applicant named above ever been under investigation for, or been found to have violated, any state or federal statute relating to child abuse or neglect, sexual misconduct or any sexual offense, including those offenses prohibited in Chapter 30, Article 3, 3A, 4, 6, 6A, 9, 37, 37 A or 52 NMSA, unless the allegations were false or unsubstantiated?"
      className={styles.radioButtonQuestion}
      options={YES_NO_OPTIONS}
      field={formData.hadBeenUnderInvestigationForChildAbuse}
      handleFieldChange={handleFieldChange}
    />

    {formData.hadBeenUnderInvestigationForChildAbuse.value && (
      <ScanByKey
        scanKey="hadBeenUnderInvestigationForChildAbuseScan"
        formData={formData}
        formId={formId}
        setIsFormBusy={setIsFormBusy}
        handleUploadScan={handleUploadScan}
        handleDeleteScan={handleDeleteScan}
      />
    )}

    <FormRadioButtonGroup
      title="Has the applicant named above ever been under investigation for, or found to have violated, any ethical rule or policy approved by a former employer that previously employed the applicant, unless the allegations were false or unsubstantiated?"
      className={styles.radioButtonQuestion}
      options={YES_NO_OPTIONS}
      field={formData.hadBeenUnderInvestigationReEthicalRule}
      handleFieldChange={handleFieldChange}
    />

    {formData.hadBeenUnderInvestigationReEthicalRule.value && (
      <ScanByKey
        scanKey="hadBeenUnderInvestigationReEthicalRuleScan"
        formData={formData}
        formId={formId}
        setIsFormBusy={setIsFormBusy}
        handleUploadScan={handleUploadScan}
        handleDeleteScan={handleDeleteScan}
      />
    )}

    <FormRadioButtonGroup
      title="Has the applicant named above ever had a professional license or certificate denied, suspended, surrendered or revoked due to a finding of child abuse or ethical misconduct or while allegations of child abuse or ethical misconduct were pending or under investigation?"
      className={styles.radioButtonQuestion}
      options={YES_NO_OPTIONS}
      field={formData.hadLicenseDenied}
      handleFieldChange={handleFieldChange}
    />

    {formData.hadLicenseDenied.value && (
      <ScanByKey
        scanKey="hadLicenseDeniedScan"
        formData={formData}
        formId={formId}
        setIsFormBusy={setIsFormBusy}
        handleUploadScan={handleUploadScan}
        handleDeleteScan={handleDeleteScan}
      />
    )}
  </div>
)

NMForm.propTypes = {
  formData: PropTypes.object.isRequired,
  handleFieldChange: PropTypes.func.isRequired,
  formId: PropTypes.string.isRequired,
  handleUploadScan: PropTypes.func.isRequired,
  handleDeleteScan: PropTypes.func.isRequired,
  setIsFormBusy: PropTypes.func.isRequired,
}

ScanByKey.propTypes = {
  scanKey: PropTypes.string.isRequired,
  formData: PropTypes.object.isRequired,
  formId: PropTypes.string.isRequired,
  handleUploadScan: PropTypes.func.isRequired,
  handleDeleteScan: PropTypes.func.isRequired,
  setIsFormBusy: PropTypes.func.isRequired,
}
